// client/src/pages/AcceptInvite.jsx
// Public page reached via /accept-invite/:token URL from an email invitation.
// Two paths:
//   1. User not signed in → redirect to sign-up with returnUrl, come back here
//   2. User signed in → POST acceptance, redirect to dashboard
//
// We render the company name + role from the invite-fetch public endpoint
// before requiring auth, so the user knows what they're accepting.

import { useState, useEffect } from 'react';
import { useAuth, SignIn, SignUp } from '@clerk/clerk-react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

export default function AcceptInvite({ token }) {
  const { isSignedIn, isLoaded, getToken, signOut } = useAuth();
  // Clerk's routing="path" mounts sub-routes (/verify-email-address,
  // /continue, /sso-callback) UNDER whatever base path it's given. Passing
  // window.location.pathname meant that after Clerk navigated to a sub-route,
  // a re-render handed it the sub-route as the new base — so it nested
  // further and lost track of the flow, stranding the invitee at a sign-in
  // screen mid-signup. The base is derived from the token so it never moves.
  const basePath = `/accept-invite/${token}`;
  // Distinguishes "wrong account signed in" from a genuinely dead invite —
  // they need completely different instructions.
  const [errorCode, setErrorCode] = useState(null);
  const [invite, setInvite] = useState(null);
  const [error, setError]   = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted]   = useState(false);
  const [authMode, setAuthMode]   = useState('sign-up');

  // Fetch invite details on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getInvite(token);
        if (!cancelled) setInvite(data.invite);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Auto-accept once signed in.
  useEffect(() => {
    if (!isSignedIn || !invite || accepted || accepting) return;
    (async () => {
      setAccepting(true);
      try {
        const tok = await getToken();
        await api.withToken(tok).acceptInvite(token);
        setAccepted(true);
        setTimeout(() => { window.location.href = '/#dashboard'; }, 1500);
      } catch (e) {
        setError(e.message);
        setErrorCode(e.code || null);
        setAccepting(false);
      }
    })();
  }, [isSignedIn, invite, accepted, accepting, getToken, token]);

  if (error) {
    // Signed in as the wrong person — recoverable, and the fix is one click.
    // Previously this showed "Invite unavailable / ask for a new invite",
    // which sent people chasing a problem that didn't exist.
    const mismatch = errorCode === 'INVITE_EMAIL_MISMATCH';
    return (
      <div style={{ minHeight: '100vh', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 40, maxWidth: 440, textAlign: 'center', border: '1px solid #E2E8F0' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>{mismatch ? '\u{1F464}' : '\u26A0\uFE0F'}</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: mismatch ? BRAND.colors.dark : '#991B1B', marginBottom: 8 }}>
            {mismatch ? 'Signed in as the wrong account' : 'Invite unavailable'}
          </div>
          <div style={{ fontSize: 13, color: '#64748B', lineHeight: 1.6 }}>{error}</div>
          {mismatch ? (
            <button onClick={async () => {
                try { if (typeof signOut === 'function') await signOut(); } catch { /* redirect anyway */ }
                window.location.reload();
              }}
              style={{ marginTop: 18, background: BRAND.colors.primary, border: 'none', borderRadius: 9,
                padding: '10px 20px', color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit' }}>
              Sign out and try again
            </button>
          ) : (
            <div style={{ marginTop: 16, fontSize: 12, color: '#94A3B8' }}>Ask your admin to send a new invite.</div>
          )}
        </div>
      </div>
    );
  }

  if (!invite || !isLoaded) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8' }}>Loading…</div>;
  }

  if (accepted) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 40, maxWidth: 440, textAlign: 'center', border: '1px solid #E2E8F0' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: BRAND.colors.dark, marginBottom: 8 }}>You're in</div>
          <div style={{ fontSize: 13, color: '#64748B' }}>Taking you to the dashboard…</div>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, flexDirection: 'column', gap: 16 }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 440, width: '100%', border: '1px solid #E2E8F0' }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: BRAND.colors.dark, marginBottom: 4 }}>Join {invite.company_name}</div>
          <div style={{ fontSize: 13, color: '#64748B', marginBottom: 18 }}>
            You've been invited to join {invite.company_name} as {invite.role === 'admin' ? 'an admin' : 'a team member'}. {authMode === 'sign-up' ? 'Create an account' : 'Sign in'} with <strong>{invite.email}</strong> to accept.
          </div>
        </div>
        {authMode === 'sign-up'
          ? <SignUp afterSignUpUrl={basePath} routing="path" path={basePath}/>
          : <SignIn afterSignInUrl={basePath} routing="path" path={basePath}/>
        }
        <button onClick={() => setAuthMode(m => m === 'sign-up' ? 'sign-in' : 'sign-up')}
          style={{ background: 'none', border: 'none', color: G, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
          {authMode === 'sign-up' ? 'Already have an account? Sign in instead' : "Don't have an account? Sign up instead"}
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 40, maxWidth: 440, textAlign: 'center', border: '1px solid #E2E8F0' }}>
        <div style={{ fontSize: 14, color: '#64748B' }}>{accepting ? 'Accepting invite…' : 'Working…'}</div>
      </div>
    </div>
  );
}
